/**
 * Compare Positive Rational Numbers generator (6.NR.1.2).
 *
 * Presents two positive rationals (in fraction and/or decimal form) and asks
 * which symbol makes the statement true. The true relation is computed with
 * Rational.compare(), so the correct choice always matches the displayed
 * values. Values are drawn from SC-allowed denominators so decimal forms
 * terminate.
 *
 * Families:
 *   F1: fraction vs decimal
 *   F2: fraction vs fraction
 *   F3: decimal  vs decimal
 */

import { rat, type Rational } from "../engine/rational";
import { Rng } from "../engine/rng";
import { getTopic } from "../curriculum/curriculum";
import type {
  Choice,
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";

const TOPIC = "compare-rationals" as const;
const VERSION = "1.0.0";

type Family = "F1" | "F2" | "F3";

interface Cfg {
  readonly families: readonly Family[];
  readonly denoms: readonly number[];
  /** Probability the two values are equal (to exercise the "=" answer). */
  readonly pEqual: number;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["F1", "F2"], denoms: [2, 4, 5, 10], pEqual: 0.15 },
  standard: { families: ["F1", "F2", "F3"], denoms: [2, 4, 5, 8, 10, 20], pEqual: 0.2 },
  hard: { families: ["F1", "F2", "F3"], denoms: [8, 20, 25, 50, 100], pEqual: 0.25 },
  challenge: { families: ["F1", "F3"], denoms: [8, 25, 50, 100], pEqual: 0.3 },
};

function placesFor(den: number): number {
  let d = den;
  let twos = 0;
  let fives = 0;
  while (d % 2 === 0) {
    d /= 2;
    twos++;
  }
  while (d % 5 === 0) {
    d /= 5;
    fives++;
  }
  return Math.max(twos, fives);
}

/** Build a positive proper-or-improper rational from an SC denominator. */
function scRational(rng: Rng, denoms: readonly number[]): Rational {
  const den = rng.pick(denoms);
  const num = rng.int(1, Math.max(2, den + Math.floor(den / 2)));
  return rat(num, den);
}

function decStr(v: Rational): string {
  // A reduced SC rational's denominator has only factors of 2 and 5, so it
  // terminates; placesFor gives the exact number of decimal places.
  return v.toNumber().toFixed(placesFor(Number(v.d)));
}

export class CompareRationalsGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = [
    "easy",
    "standard",
    "hard",
    "challenge",
  ];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const cfg = DIFF[ctx.difficulty];
      const family = rng.pick(cfg.families);

      let a = scRational(rng, cfg.denoms);
      let b: Rational;
      if (rng.bool(cfg.pEqual)) {
        // Force an equal pair via a different-but-equivalent form.
        b = a;
      } else {
        b = scRational(rng, cfg.denoms);
      }

      // Display forms per family.
      let aDisp: string;
      let bDisp: string;
      switch (family) {
        case "F1":
          aDisp = a.toString();
          bDisp = decStr(b);
          break;
        case "F2":
          aDisp = a.toString();
          bDisp = b.toString();
          break;
        case "F3":
          aDisp = decStr(a);
          bDisp = decStr(b);
          break;
      }

      const cmp = a.compare(b);
      const correctId = cmp < 0 ? "lt" : cmp > 0 ? "gt" : "eq";

      const choices: Choice[] = [
        { id: "lt", label: "<", latex: "<" },
        { id: "gt", label: ">", latex: ">" },
        { id: "eq", label: "=", latex: "=" },
      ];

      const symbol = cmp < 0 ? "<" : cmp > 0 ? ">" : "=";
      const promptText = `Which symbol makes the statement true?  ${aDisp} __ ${bDisp}`;

      return {
        id: `${TOPIC}-${ctx.seed}-${family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family,
        difficulty: ctx.difficulty,
        prompt: {
          text: promptText,
          latex: `${a.toLatex()} \\;\\square\\; ${b.toLatex()}`,
        },
        answerType: "choice",
        answer: { kind: "choice", value: correctId },
        steps: [
          {
            description:
              "Rewrite both numbers in the same form (a common denominator or decimals), then compare.",
            latex: `${a.toLatex()} ${symbol} ${b.toLatex()}`,
          },
        ],
        hints: [
          { level: 1, text: "Put both numbers in the same form before comparing." },
          {
            level: 2,
            text: "A common denominator lets you compare numerators; decimals let you compare place by place.",
          },
        ],
        subskills: topic.subskills,
        choices,
      };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const structure = q.answer.kind === "choice" ? q.answer.value : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const compareRationals = new CompareRationalsGenerator();
